import { BaseService } from './api'
import type {
  NotificationSettings,
  NotificationEventType,
  NotificationPreference,
  Notification,
  NotificationSettingsResponse,
  NotificationEventTypesResponse,
  NotificationPreferencesResponse,
  NotificationsResponse
} from '../types/notifications'

class NotificationService extends BaseService {
  // Get notification settings
  async getSettings(): Promise<NotificationSettings> {
    const response = await this.get<NotificationSettingsResponse>('/notifications/settings')
    if (!response.data) {
      throw new Error('Failed to fetch notification settings')
    }
    return response.data
  }

  // Update notification settings
  async updateSettings(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    const response = await this.put<NotificationSettingsResponse>('/notifications/settings', settings)
    if (!response.data) {
      throw new Error('Failed to update notification settings')
    }
    return response.data
  }

  // Get notification event types
  async getEventTypes(): Promise<NotificationEventType[]> {
    const response = await this.get<NotificationEventTypesResponse>('/notifications/event-types')
    if (!response.data) {
      throw new Error('Failed to fetch notification event types')
    }
    return response.data
  }

  // Get notification preferences
  async getPreferences(): Promise<NotificationPreference[]> {
    const response = await this.get<NotificationPreferencesResponse>('/notifications/preferences')
    if (!response.data) {
      throw new Error('Failed to fetch notification preferences')
    }
    return response.data
  }

  // Update notification preferences
  async updatePreferences(preferences: NotificationPreference[]): Promise<NotificationPreference[]> {
    const response = await this.put<NotificationPreferencesResponse>('/notifications/preferences', { preferences })
    if (!response.data) {
      throw new Error('Failed to update notification preferences')
    }
    return response.data
  }

  // Get notification list
  async getNotifications(): Promise<Notification[]> {
    const response = await this.get<NotificationsResponse>('/notifications')
    if (!response.data) {
      throw new Error('Failed to fetch notifications')
    }
    return response.data
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    await this.put(`/notifications/${notificationId}/read`, {})
  }

  // Mark all notifications as read
  async markAllAsRead(): Promise<void> {
    await this.put('/notifications/read-all', {})
  }

  // Delete notification
  async deleteNotification(notificationId: string): Promise<void> {
    await this.delete(`/notifications/${notificationId}`)
  }

  // Delete all notifications
  async deleteAllNotifications(): Promise<void> {
    await this.delete('/notifications')
  }
}

export const notificationService = new NotificationService() 